import type { DirectoryUser } from "@/lib/types";
import {
  ActionConfirmationModal,
  Badge,
  Button,
  TextWithMentions,
  Typography,
} from "@/components/ui";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/shadcn/Accordion/Accordion";
import { UserProfilePhoto } from "@/components/ui/UserProfilePhoto/UserProfilePhoto";
import { useTiptapMentions } from "@/hooks/useTiptapMentions";
import {
  useCreateContactNoteComment,
  useDeleteContactNoteComment,
  useUpdateContactNoteComment,
} from "@/lib/data/react-query";
import { type ContactNoteComment, type User } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { convertToLocalFromDatabaseDateTime } from "@/lib/utils/dateUtils";
import { formatDateTime, formatUsersName } from "@/lib/utils/format";
import { type MentionOption } from "@/lib/utils/mentionUtils";
import { EditorContent } from "@tiptap/react";
import { Pencil, Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";

const COMMENT_MAX_LENGTH = 5000;

type ContactNoteCommentsProps = {
  contactId: string;
  noteId: string;
  comments?: ContactNoteComment[];
  users: DirectoryUser[];
  mentionOptions: MentionOption[];
  isUsersLoading: boolean;
  currentUser: User;
  canCreateComment: boolean;
  canEditComment: boolean;
  canDeleteComment: boolean;
  className?: string;
};

const buildCommentUser = (
  comment: ContactNoteComment,
  users: DirectoryUser[],
) => {
  const commentUserId = comment.author?.id ?? comment.authorUserId;
  if (comment.author?.firstName && comment.author.lastName) {
    return {
      id: commentUserId ?? "",
      firstName: comment.author.firstName,
      lastName: comment.author.lastName,
      profilePicturePresignedUrlKey: undefined,
    };
  }

  return users.find((user) => user.id === commentUserId);
};

const ContactNoteCommentMessage = ({
  text,
  users,
}: {
  text: string;
  users: DirectoryUser[];
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const textContentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setIsExpanded(false);
  }, [text]);

  useEffect(() => {
    const element = textContentRef.current;
    if (!element) {
      setHasOverflow(false);
      return;
    }

    const determineOverflow = () => {
      if (isExpanded) {
        setHasOverflow(true);
        return;
      }
      const tolerancePx = 2;
      setHasOverflow(element.scrollHeight - element.clientHeight > tolerancePx);
    };

    determineOverflow();

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      if (!isExpanded) {
        determineOverflow();
      }
    });
    resizeObserver.observe(element);
    return () => {
      resizeObserver.disconnect();
    };
  }, [text, isExpanded]);

  return (
    <div className="mt-2 flex flex-col gap-2">
      <Typography
        ref={textContentRef}
        size="sm"
        className={cn(
          "text-muted-foreground whitespace-pre-wrap",
          !isExpanded && "line-clamp-4",
        )}
      >
        <TextWithMentions text={text} users={users} />
      </Typography>
      {hasOverflow && (
        <button
          type="button"
          onClick={() => {
            setIsExpanded((prev) => !prev);
          }}
          className="text-primary dark:text-foreground w-fit text-xs font-medium hover:underline"
        >
          {isExpanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
};

export const ContactNoteComments = ({
  contactId,
  noteId,
  comments,
  users,
  mentionOptions,
  isUsersLoading,
  currentUser,
  canCreateComment,
  canEditComment,
  canDeleteComment,
  className,
}: ContactNoteCommentsProps) => {
  const createContactNoteComment = useCreateContactNoteComment();
  const updateContactNoteComment = useUpdateContactNoteComment();
  const deleteContactNoteComment = useDeleteContactNoteComment();

  const [commentInput, setCommentInput] = useState("");
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [commentEditInput, setCommentEditInput] = useState("");
  const [commentToDelete, setCommentToDelete] =
    useState<ContactNoteComment | null>(null);
  const lastSyncedCommentEditRef = useRef<{
    commentId: string;
    message: string;
  } | null>(null);

  const noteComments = useMemo(() => {
    const filtered = (comments ?? []).filter(
      (comment) => comment.noteId === noteId,
    );
    return filtered.sort((a, b) => {
      const aTimestamp = a.dateCreated ? new Date(a.dateCreated).getTime() : 0;
      const bTimestamp = b.dateCreated ? new Date(b.dateCreated).getTime() : 0;
      return aTimestamp - bTimestamp;
    });
  }, [comments, noteId]);

  const commentCount = noteComments.length;

  const {
    editor: commentEditor,
    getBackendMessage: getCommentBackendMessage,
    setContent: setCommentContent,
    mentionDropdownOpenRef: commentMentionDropdownOpenRef,
  } = useTiptapMentions({
    mentionOptions,
    isLoading: isUsersLoading,
    onUpdate: (value) => {
      setCommentInput(value);
    },
    isEditable: canCreateComment,
    className:
      "max-h-[200px] min-h-[100px] overflow-y-auto border-0 bg-transparent px-4 py-3 text-sm shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none",
    placeholder: "Add a comment...",
  });

  const {
    editor: editCommentEditor,
    getBackendMessage: getEditCommentBackendMessage,
    setContent: setEditCommentContent,
    mentionDropdownOpenRef: editCommentMentionDropdownOpenRef,
  } = useTiptapMentions({
    mentionOptions,
    isLoading: isUsersLoading,
    onUpdate: (value) => {
      setCommentEditInput(value);
    },
    isEditable: Boolean(editingCommentId),
    className:
      "max-h-[200px] min-h-[100px] overflow-y-auto border-0 bg-transparent px-4 py-3 text-sm shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none",
    placeholder: "Update comment...",
  });

  const previousEditingCommentIdRef = useRef<string | null>(null);
  useEffect(() => {
    const previousEditingCommentId = previousEditingCommentIdRef.current;
    previousEditingCommentIdRef.current = editingCommentId;

    if (!editingCommentId) {
      if (!previousEditingCommentId) {
        return;
      }

      setCommentEditInput("");
      setEditCommentContent("");
      lastSyncedCommentEditRef.current = null;
      return;
    }

    const activeComment = noteComments.find(
      (comment) => comment.id === editingCommentId,
    );

    if (!activeComment) {
      setEditingCommentId(null);
      return;
    }

    const lastSnapshot = lastSyncedCommentEditRef.current;
    const nextSnapshot = {
      commentId: activeComment.id,
      message: activeComment.message ?? "",
    };

    if (
      lastSnapshot &&
      lastSnapshot.commentId === nextSnapshot.commentId &&
      lastSnapshot.message === nextSnapshot.message
    ) {
      return;
    }

    lastSyncedCommentEditRef.current = nextSnapshot;
    setCommentEditInput(activeComment.message ?? "");
    setEditCommentContent(activeComment.message ?? "");
  }, [
    editingCommentId,
    noteComments,
    setCommentEditInput,
    setEditCommentContent,
    setEditingCommentId,
  ]);

  const canSubmitComment =
    canCreateComment &&
    Boolean(commentInput.trim()) &&
    !createContactNoteComment.isPending;

  const handleAddComment = async () => {
    if (!canSubmitComment) {
      return;
    }

    const previousCommentInput = commentInput;
    const backendMessage = getCommentBackendMessage();
    if (backendMessage.length > COMMENT_MAX_LENGTH) {
      toast.error("Message must be 5000 characters or less");
      return;
    }

    setCommentContent("");
    setCommentInput("");

    try {
      await createContactNoteComment.mutateAsync({
        contactId,
        noteId,
        message: backendMessage,
      });
    } catch {
      setCommentContent(backendMessage);
      setCommentInput(previousCommentInput);
    }
  };

  const handleStartEditComment = (comment: ContactNoteComment) => {
    if (
      !(
        canEditComment ||
        (comment.author?.id ?? comment.authorUserId) === currentUser.id
      )
    ) {
      return;
    }

    setEditingCommentId(comment.id);
    setCommentEditInput(comment.message ?? "");
    setEditCommentContent(comment.message ?? "");
  };

  const handleCancelEditComment = () => {
    setEditingCommentId(null);
    setCommentEditInput("");
    setEditCommentContent("");
    lastSyncedCommentEditRef.current = null;
  };

  const handleSaveComment = async () => {
    if (!editingCommentId || !commentEditInput.trim()) {
      return;
    }

    const activeComment = noteComments.find(
      (comment) => comment.id === editingCommentId,
    );

    if (
      !activeComment ||
      !(
        canEditComment ||
        (activeComment.author?.id ?? activeComment.authorUserId) ===
          currentUser.id
      )
    ) {
      setEditingCommentId(null);
      return;
    }

    const backendMessage = getEditCommentBackendMessage();
    if (backendMessage.length > COMMENT_MAX_LENGTH) {
      toast.error("Message must be 5000 characters or less");
      return;
    }
    if (backendMessage === activeComment.message) {
      setEditingCommentId(null);
      setCommentEditInput("");
      setEditCommentContent("");
      lastSyncedCommentEditRef.current = null;
      return;
    }

    try {
      await updateContactNoteComment.mutateAsync({
        contactId,
        noteId,
        id: activeComment.id,
        dateModified: activeComment.dateModified,
        message: backendMessage,
      });
      setEditingCommentId(null);
      setCommentEditInput("");
      setEditCommentContent("");
      lastSyncedCommentEditRef.current = null;
    } catch {
      // errors handled in mutation
    }
  };

  const handleDeleteComment = async () => {
    if (!commentToDelete) {
      return;
    }

    try {
      await deleteContactNoteComment.mutateAsync({
        contactId,
        noteId,
        id: commentToDelete.id,
      });

      if (editingCommentId === commentToDelete.id) {
        setEditingCommentId(null);
      }
    } finally {
      setCommentToDelete(null);
    }
  };

  const commentsSection = (
    <Accordion
      type="single"
      collapsible
      className={cn("border-border/60 mt-4 border-t pt-2", className)}
    >
      <AccordionItem value="note-comments" className="border-b-0">
        <AccordionTrigger className="px-0 py-2 hover:no-underline">
          <div className="flex w-full items-center justify-between">
            <Typography variant="subHeading" className="text-muted-foreground">
              Comments
            </Typography>
            <Badge variant="secondary" className="h-5 px-2 text-xs">
              {commentCount}
            </Badge>
          </div>
        </AccordionTrigger>
        <AccordionContent className="flex flex-col gap-3 px-0">
          {commentCount === 0 && (
            <Typography size="sm" className="text-muted-foreground">
              No comments yet.
            </Typography>
          )}
          {commentCount > 0 && !isUsersLoading && (
            <div className="flex flex-col gap-3">
              {noteComments.map((comment) => {
                const isEditingComment = editingCommentId === comment.id;
                const isCommentOwner =
                  (comment.author?.id ?? comment.authorUserId) ===
                  currentUser.id;
                const canEditThisComment = canEditComment || isCommentOwner;
                const canDeleteThisComment = canDeleteComment || isCommentOwner;
                const showCommentActions =
                  !isEditingComment &&
                  (canEditThisComment || canDeleteThisComment);
                const canSaveEditedComment =
                  isEditingComment &&
                  Boolean(commentEditInput.trim()) &&
                  canEditThisComment &&
                  !updateContactNoteComment.isPending;
                const commentUser = buildCommentUser(comment, users);
                const formattedDate = formatDateTime(
                  convertToLocalFromDatabaseDateTime(
                    comment.publishedDate ?? comment.dateCreated,
                  ) ?? "",
                  false,
                );

                return (
                  <article
                    key={comment.id}
                    className="border-border bg-card/80 relative flex gap-3 rounded-xl border p-3 shadow-sm"
                  >
                    {showCommentActions && (
                      <div className="absolute top-2 right-2 flex gap-1">
                        {canEditThisComment && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => {
                              handleStartEditComment(comment);
                            }}
                            aria-label="Edit comment"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {canDeleteThisComment && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive h-8 w-8"
                            onClick={() => {
                              setCommentToDelete(comment);
                            }}
                            aria-label="Delete comment"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    )}
                    <UserProfilePhoto user={commentUser} className="h-8 w-8" />
                    <div className="min-w-0 flex-1">
                      <div className="text-muted-foreground flex flex-wrap items-center gap-x-2 pr-12 text-xs">
                        <span className="text-foreground font-medium">
                          {formatUsersName(commentUser) ?? "Unknown User"}
                        </span>
                        <span className="text-muted-foreground">•</span>
                        <span>{formattedDate ?? ""}</span>
                      </div>
                      {isEditingComment ? (
                        <div className="border-border bg-card mt-2 rounded-xl border shadow-sm">
                          <EditorContent
                            editor={editCommentEditor}
                            onKeyDown={(event) => {
                              if (
                                event.key !== "Enter" ||
                                event.shiftKey ||
                                event.altKey ||
                                event.metaKey ||
                                event.ctrlKey
                              ) {
                                return;
                              }

                              if (
                                editCommentMentionDropdownOpenRef.current ||
                                !canSaveEditedComment
                              ) {
                                return;
                              }

                              event.preventDefault();
                              event.stopPropagation();
                              void handleSaveComment();
                            }}
                          />
                          <div className="border-border flex items-center justify-end gap-2 border-t px-4 py-2.5">
                            <Button
                              type="button"
                              variant="outline"
                              onClick={handleCancelEditComment}
                              disabled={updateContactNoteComment.isPending}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              onClick={() => {
                                void handleSaveComment();
                              }}
                              disabled={!canSaveEditedComment}
                            >
                              Update
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <ContactNoteCommentMessage
                          text={comment.message ?? ""}
                          users={users}
                        />
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
          {canCreateComment && (
            <div className="border-border bg-card overflow-visible rounded-xl border shadow-sm">
              <EditorContent
                editor={commentEditor}
                onKeyDown={(event) => {
                  if (
                    event.key !== "Enter" ||
                    event.shiftKey ||
                    event.altKey ||
                    event.metaKey ||
                    event.ctrlKey
                  ) {
                    return;
                  }

                  if (commentMentionDropdownOpenRef.current) {
                    return;
                  }

                  if (!canSubmitComment) {
                    return;
                  }

                  event.preventDefault();
                  event.stopPropagation();
                  void handleAddComment();
                }}
              />
              <div className="flex items-center justify-end gap-2 px-4 py-2.5">
                <Button
                  type="button"
                  onClick={() => {
                    void handleAddComment();
                  }}
                  disabled={!canSubmitComment}
                >
                  Add comment
                </Button>
              </div>
            </div>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );

  return (
    <>
      {commentsSection}
      <ActionConfirmationModal
        title="Are you sure you want to delete this comment?"
        open={Boolean(commentToDelete)}
        setOpen={(open) => {
          if (!open) {
            setCommentToDelete(null);
          }
        }}
        afterActionFn={() => {
          void handleDeleteComment();
        }}
      />
    </>
  );
};
